import * as Sentry from '@sentry/react';
import { db, CachedSpraySession, CachedSprayProduct } from '../db';
import {
  sprayService,
  SpraySession,
  SprayProduct,
  SprayProgramRef,
} from './sprayService';

/**
 * Spray session local cache for Field Mode.
 *
 * Reads and writes to the Dexie spraySessions table (introduced in v9 by
 * Phase 4 of the spray_session_workflow_20260427 track). Provides four
 * primitives that the rest of the offline pipeline composes:
 *
 *   refreshMySessions()       — pull from /sessions/my, merge with the
 *                               cache, preserving rows whose _id has a
 *                               pending entry in either offline queue.
 *   readCachedSessions()      — for OperatorSessionList; returns the rows
 *                               sorted by date ascending.
 *   applyLocalStartedSession  — optimistically flip a cached row to
 *                               in_progress at the device's start time.
 *   purgeCompletedSession     — drop a cached row after a successful
 *                               /complete sync.
 *
 * The merge invariant: a row with a pending Start (spraySessionStarts) or
 * pending Complete (spraySignOffs) is the operator's local truth and
 * must NOT be overwritten by a refresh — the queue still has to drain.
 */

const CACHEABLE_STATUSES: ReadonlyArray<CachedSpraySession['status']> = [
  'planned',
  'in_progress',
];

function isCacheableStatus(s: SpraySession['status']): s is CachedSpraySession['status'] {
  return s !== undefined && (CACHEABLE_STATUSES as ReadonlyArray<string>).includes(s);
}

/**
 * Pull the program ref into the cache shape. /sessions/my always
 * populates programId; the raw-string branch is a defensive fallback.
 */
function pickProgramRef(programId: SpraySession['programId']): { _id: string; name: string } {
  if (programId && typeof programId === 'object' && '_id' in programId) {
    const ref = programId as SprayProgramRef;
    return { _id: String(ref._id), name: ref.name ?? '' };
  }
  return { _id: String(programId), name: '' };
}

function pickMachineId(machineId: SpraySession['machineId']): string | undefined {
  if (!machineId) return undefined;
  if (typeof machineId === 'string') return machineId;
  return (machineId as { _id?: string })._id;
}

function pickOperatorIds(operatorIds: SpraySession['operatorIds']): string[] | undefined {
  if (!operatorIds) return undefined;
  return operatorIds.map(o =>
    typeof o === 'string' ? o : String((o as { _id?: string })._id)
  );
}

/**
 * Translate a wire SpraySession into the cache shape. Drops fields the
 * Field Mode UI never reads.
 */
function toCached(s: SpraySession, cachedAt: string): CachedSpraySession | null {
  const status = s.status ?? 'planned';
  if (!isCacheableStatus(status)) return null;
  return {
    _id: s._id,
    tenantId: s.tenantId,
    programId: pickProgramRef(s.programId),
    name: s.name,
    orchardIds: s.orchardIds ?? [],
    date: s.date,
    startDate: s.startDate,
    endDate: s.endDate,
    productsConsumed: (s.productsConsumed ?? []).map(p => ({
      productId: typeof p.productId === 'string'
        ? p.productId
        : String((p.productId as { _id?: string })._id),
      quantity: p.quantity,
      // Preserve plannedQuantity so an offline-loaded completion form
      // can still show "(planned: 4500 g)" hints next to the input.
      ...(p.plannedQuantity != null ? { plannedQuantity: p.plannedQuantity } : {}),
    })),
    machineId: pickMachineId(s.machineId),
    operatorIds: pickOperatorIds(s.operatorIds),
    status,
    intensityLevel: s.intensityLevel,
    ppeChecklist: s.ppeChecklist,
    createdAt: s.createdAt,
    cachedAt,
  };
}

/**
 * Collect the set of session IDs that have at least one pending entry
 * in either offline queue. Refresh treats these as locally-owned and
 * does not overwrite or delete them.
 */
async function pendingQueueSessionIds(): Promise<Set<string>> {
  const [starts, signOffs] = await Promise.all([
    db.spraySessionStarts.where('syncStatus').anyOf(['pending', 'failed', 'syncing']).toArray(),
    db.spraySignOffs.where('syncStatus').anyOf(['pending', 'failed', 'syncing']).toArray(),
  ]);
  const ids = new Set<string>();
  for (const e of starts) ids.add(e.sessionId);
  for (const e of signOffs) ids.add(e.sessionId);
  return ids;
}

export async function readCachedSessions(): Promise<CachedSpraySession[]> {
  const rows = await db.spraySessions.toArray();
  rows.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  return rows;
}

/**
 * Pull /sessions/my and reconcile the local cache:
 *   - Server rows whose _id is locally pending → leave the cached row as-is.
 *   - Server rows otherwise → upsert in place.
 *   - Cached rows the server omits → delete, UNLESS locally pending
 *     (those wait for the queue drainer to resolve).
 */
export async function refreshMySessions(): Promise<{ hasLinkedOperator: boolean }> {
  return Sentry.startSpan(
    { name: 'spray.cache.refreshMySessions', op: 'cache.sync' },
    async () => {
      try {
        const { sessions, hasLinkedOperator } = await sprayService.getMySessions();
        const pendingIds = await pendingQueueSessionIds();
        const now = new Date().toISOString();

        const existing = await db.spraySessions.toArray();
        const serverIds = new Set<string>();
        const toUpsert: CachedSpraySession[] = [];
        for (const wire of sessions) {
          serverIds.add(wire._id);
          if (pendingIds.has(wire._id)) continue;
          const cached = toCached(wire, now);
          if (cached) toUpsert.push(cached);
        }
        if (toUpsert.length) await db.spraySessions.bulkPut(toUpsert);

        const toDelete: string[] = [];
        for (const row of existing) {
          if (serverIds.has(row._id)) continue;
          if (pendingIds.has(row._id)) continue;
          toDelete.push(row._id);
        }
        if (toDelete.length) await db.spraySessions.bulkDelete(toDelete);

        return { hasLinkedOperator };
      } catch (err) {
        Sentry.captureException(err);
        throw err;
      }
    }
  );
}

/**
 * Set of sessionIds with at least one offline-queued sign-off in any
 * non-synced state. Drives the "Completed (Unsynced)" badge in
 * OperatorSessionList and the read-only form render in
 * FieldModeCompletionForm.
 */
export async function getPendingSignOffSessionIds(): Promise<Set<string>> {
  const rows = await db.spraySignOffs
    .where('syncStatus')
    .anyOf(['pending', 'failed', 'syncing', 'quarantined'])
    .toArray();
  const ids = new Set<string>();
  for (const r of rows) ids.add(r.sessionId);
  return ids;
}

/**
 * Most recent queued sign-off for a session, if any. The completion
 * form layers this payload over its defaults so the read-only render
 * shows what the operator actually submitted (not the editable
 * defaults). `createdAt` desc — if multiple queue entries exist for
 * the same session for any reason, the latest one is the one that
 * will eventually drain.
 */
export async function getPendingSignOff(sessionId: string) {
  const rows = await db.spraySignOffs
    .where('sessionId')
    .equals(sessionId)
    .filter(r =>
      r.syncStatus === 'pending' ||
      r.syncStatus === 'failed' ||
      r.syncStatus === 'syncing' ||
      r.syncStatus === 'quarantined'
    )
    .toArray();
  if (rows.length === 0) return null;
  rows.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  return rows[0];
}

/**
 * Reshape a cached row back into a SpraySession-shaped object so the
 * Field Mode forms can consume it without knowing whether the data
 * came from the network or the cache. The fields the forms actually
 * read (programId, productsConsumed, ppeChecklist, startDate, status,
 * etc.) are all preserved by the cache, so this is a near-direct
 * pass-through; we widen the typed shape and reattach the productId
 * Mongoose-string vs populated-doc duality.
 */
function fromCached(row: CachedSpraySession): SpraySession {
  return {
    _id: row._id,
    tenantId: row.tenantId,
    programId: row.programId,
    name: row.name,
    orchardIds: row.orchardIds,
    date: row.date,
    startDate: row.startDate,
    endDate: row.endDate,
    productsConsumed: row.productsConsumed.map(p => ({
      productId: p.productId,
      quantity: p.quantity,
      ...(p.plannedQuantity != null ? { plannedQuantity: p.plannedQuantity } : {}),
    })),
    machineId: row.machineId,
    operatorIds: row.operatorIds,
    status: row.status,
    intensityLevel: row.intensityLevel,
    ppeChecklist: row.ppeChecklist,
    createdAt: row.createdAt,
  } as SpraySession;
}

/**
 * Single-session load with offline-aware fallback. Online callers get
 * the freshest /sessions/:id payload (with consumption transactions);
 * offline callers — or those who hit a transient network error — fall
 * back to the local cache so the operator can still tap a cached row
 * and act on it.
 *
 * The cached row carries everything the Field Mode forms read; we just
 * don't have consumptionTransactions when offline (they aren't cached
 * because the forms ignore them entirely on the operator path).
 */
export async function getSessionWithCacheFallback(
  sessionId: string
): Promise<{ session: SpraySession }> {
  try {
    const { session } = await sprayService.getSession(sessionId);
    return { session };
  } catch (err: any) {
    // Only fall back when this looks like a network-layer failure. A
    // backend 4xx/5xx is a real signal — e.g., 404 means the session
    // was deleted, and silently substituting the cache would mask
    // legitimate state divergence.
    const hasBackendResponse = !!(err?.response || err?.cause?.response);
    if (hasBackendResponse) throw err;
    const cached = await db.spraySessions.get(sessionId);
    if (cached) return { session: fromCached(cached) };
    throw err;
  }
}

/**
 * Optimistically flip a cached session to in_progress. Called by the
 * Start service immediately after queueing — keeps the UI responsive
 * while the network call is in flight or the queue waits to drain.
 */
export async function applyLocalStartedSession(
  sessionId: string,
  startDate: string
): Promise<void> {
  const row = await db.spraySessions.get(sessionId);
  if (!row) return;
  await db.spraySessions.put({
    ...row,
    status: 'in_progress',
    startDate,
    cachedAt: new Date().toISOString(),
  });
}

/**
 * Drop a cached row after the /complete sync succeeds. The completed
 * session never returns from /sessions/my again, so removing it locally
 * keeps the cache aligned with the server.
 */
export async function purgeCompletedSession(sessionId: string): Promise<void> {
  await db.spraySessions.delete(sessionId);
}

/**
 * Slim a wire SprayProduct down to the fields the Field Mode form
 * actually reads. Discarding catalog metadata (PDFs, label refs, etc.)
 * keeps the offline cache small.
 */
function toCachedProduct(p: SprayProduct, cachedAt: string): CachedSprayProduct {
  return {
    _id: p._id,
    productName: p.productName,
    dosePer100LUnit: p.dosePer100LUnit,
    cachedAt,
  };
}

/**
 * Network-first product catalog fetch with a Dexie fallback for the
 * offline path. Without this, an operator who re-opens a queued
 * sign-off offline sees raw productId strings instead of human names
 * — the form joins productsConsumed[].productId against this catalog.
 *
 * Successful network fetches always rewrite the cache with a fresh
 * `cachedAt`; the cache is replaced wholesale (not merged) since the
 * server is authoritative on the catalog. Failures fall back to the
 * cache; an empty cache surfaces an empty list rather than throwing,
 * matching the existing form's "best-effort, falls back to ID" UX.
 */
export async function getProductsWithCacheFallback(): Promise<SprayProduct[]> {
  try {
    const list = await sprayService.getProducts();
    const now = new Date().toISOString();
    const cached = list.map(p => toCachedProduct(p, now));
    // Replace-not-merge: if the admin removes a product, the cache
    // shouldn't keep serving its stale name to operators offline.
    await db.sprayProducts.clear();
    if (cached.length) await db.sprayProducts.bulkPut(cached);
    return list;
  } catch (err: any) {
    const hasBackendResponse = !!(err?.response || err?.cause?.response);
    if (hasBackendResponse) throw err;
    const cached = await db.sprayProducts.toArray();
    // Surface as the SprayProduct shape — extra fields are absent but
    // the form only reads productName and dosePer100LUnit.
    return cached.map(c => ({
      _id: c._id,
      productName: c.productName,
      lNumber: '',
      tenantId: null,
      activeIngredients: [] as any,
      dosePer100LUnit: c.dosePer100LUnit as any,
    }));
  }
}

/**
 * Test helper — exposes the cache-clear primitive so tests don't have
 * to import db directly. Also handy if a future operator-switch flow
 * needs to invalidate the cache.
 */
export async function __clearSpraySessionCacheForTesting(): Promise<void> {
  await db.spraySessions.clear();
  await db.sprayProducts.clear();
}
